import React, { useEffect, useState } from 'react';
import {
  MdPerson, MdSchool, MdBadge, MdDirectionsBus, MdLogout, MdPhone, MdCalendarMonth,
} from 'react-icons/md';
import { Card, EmptyState } from '../../ui/Card';
import { dateLabel, money } from '../../ui/format';
import { useStudentData } from '../StudentDataContext';
import { useAuth } from '../auth';
import api from '../../ui/api';
import AboutSchool from '../AboutSchool';
import { yearLabel } from '../about';
import './pages.css';

const GENDERS = { male: 'ذكر', female: 'أنثى' };

/** One label/value line; renders nothing when the value is empty, so a
 *  sparse record shows a short clean card instead of rows of dashes. */
function Row({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <li className="row">
      <div className="row-main"><div className="row-label">{label}</div></div>
      <span className="row-value">{value}</span>
    </li>
  );
}

/** 60000 → "#060000"; old text ids stay as they are. */
const regNo = (v) => (v && /^\d+$/.test(String(v)) ? `#${String(v).padStart(6, '0')}` : v);

export default function Profile() {
  const { profile } = useStudentData();
  const { user, parentType, logout } = useAuth();
  const [year, setYear] = useState(null);
  useEffect(() => {
    api.academicYears().then((list) => setYear(list?.[0] || null)).catch(() => {});
  }, []);

  if (!profile) {
    return (
      <div className="page">
        <h1 className="page-title">الملف الشخصي</h1>
        <Card><EmptyState icon={MdPerson} title="لا توجد بيانات" /></Card>
      </div>
    );
  }

  const initials = (profile.name || '؟').trim().charAt(0);
  const viewerNote = parentType === 'father' ? 'تسجيل الدخول كولي أمر (الأب)'
    : parentType === 'mother' ? 'تسجيل الدخول كولي أمر (الأم)'
    : null;

  return (
    <div className="page">
      <h1 className="page-title">الملف الشخصي</h1>

      <div className="profile-head">
        <div className="profile-avatar">{initials}</div>
        <h2>{profile.name}</h2>
        <p>{[profile.grade_title, profile.section_label].filter(Boolean).join(' — ') || 'غير مسجّل في شعبة'}</p>
        {viewerNote && <span className="profile-viewer">{viewerNote}</span>}
        {year && (
          <div className="profile-year">
            <MdCalendarMonth /> العام الدراسي <strong dir="ltr">{yearLabel(year.title)}</strong>
          </div>
        )}
      </div>

      <Card title="البيانات الأساسية" icon={MdSchool} padded={false}>
        <ul className="row-list">
          <Row label="الصف" value={profile.grade_title} />
          <Row label="الشعبة" value={profile.section_label} />
          <Row label="الجنس" value={GENDERS[profile.gender]} />
          <Row label="تاريخ الميلاد" value={profile.birth_date ? dateLabel(profile.birth_date) : null} />
          <Row label="مكان الميلاد" value={profile.birth_place} />
          <Row label="عدد الإخوة في المدرسة" value={profile.sibling_count || null} />
        </ul>
      </Card>

      <Card title="بيانات التواصل" icon={MdPhone} padded={false}>
        <ul className="row-list">
          <Row label="هاتف الطالب" value={profile.student_phone} />
          <Row label="الحالة الصحية" value={profile.health_status} />
        </ul>
        {!profile.student_phone && !profile.health_status && (
          <EmptyState icon={MdPhone} title="لا توجد بيانات تواصل مسجّلة" />
        )}
      </Card>

      {profile.transportation_id ? (
        <Card title="المواصلات" icon={MdDirectionsBus} padded={false}>
          <ul className="row-list">
            <Row label="قسط النقل الشهري" value={money(profile.transport_fee)} />
          </ul>
        </Card>
      ) : null}

      <Card title="البيانات الإدارية" icon={MdBadge} padded={false}>
        <ul className="row-list">
          <Row label="الرقم الوطني" value={profile.national_id} />
          <Row label="رقم التسجيل" value={regNo(profile.registration_number)} />
          <Row label="تاريخ التسجيل" value={profile.registration_date ? dateLabel(profile.registration_date) : null} />
        </ul>
      </Card>

      <p className="page-note">
        لتعديل أي من هذه البيانات، يرجى مراجعة مكتب المدرسة.
      </p>

      <button type="button" className="ui-btn ui-btn-secondary ui-btn-block logout-btn" onClick={logout}>
        <MdLogout /> تسجيل الخروج{user?.name ? ` (${user.name})` : ''}
      </button>

      <AboutSchool />
    </div>
  );
}
